// Auth context: tracks the current Firebase user + the linked users/{uid} profile doc.
//
// Two reasons we mirror the user doc into context:
//   1. The web client needs `role` to decide whether to show admin UI without
//      having to read the ID token claim every render.
//   2. `freeQuestionsRemaining` and `subscriptionStatus` drive the paywall
//      banner — having them in context avoids per-page re-fetches.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendEmailVerification,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { callBootstrapUserProfile } from './api';

// Verification email path:
//   Branded (admin@cnapractice.com via Namecheap SMTP + custom template):
//     `callSendVerificationEmail({})` — currently DISABLED until the
//     SMTP_PASSWORD secret is set to the real mailbox password. The
//     placeholder value (`PLACEHOLDER_REPLACE_ME`) caused nodemailer to fail
//     auth and no email was reaching the user. Re-enable by:
//       firebase functions:secrets:set SMTP_PASSWORD
//     and flipping `USE_BRANDED_VERIFICATION_EMAIL` to true below.
//   Default (noreply@<project>.firebaseapp.com):
//     `sendEmailVerification(auth.currentUser)` — works without any extra
//     setup. Used as the fallback path right now so users get a verification
//     email at all.
const USE_BRANDED_VERIFICATION_EMAIL = true;

export interface UserProfile {
  email: string;
  displayName?: string;
  phoneNumber?: string;
  role: 'user' | 'admin';
  onboardingComplete: boolean;
  onboardingState?: 'account_created' | 'email_verified' | 'profile_complete' | 'diagnostic_not_started' | 'diagnostic_in_progress' | 'diagnostic_completed' | 'adaptive_profile_initialized';
  diagnosticRequired?: boolean;
  diagnosticStatus?: 'not_started' | 'in_progress' | 'completed';
  activeDiagnosticSessionId?: string;
  freeQuestionsRemaining: number;
  subscriptionStatus: 'free_trial' | 'active' | 'canceled' | 'past_due' | 'expired';
  subscriptionTier?: 'monthly' | 'quarterly' | 'one_time';
  subscriptionEndsAt?: number;
  state?: string;
  targetExamDate?: number;
}

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  profileIsAdmin: boolean;
  tokenIsAdmin: boolean | null;
  adminClaimLoading: boolean;
  adminClaimError: string | null;
  isAnonymous: boolean;
  needsEmailVerification: boolean;
  // True only when displayName, phoneNumber, and state are all set on the
  // profile. Drives the route-level redirect that pins the user to /account
  // until they finish filling in required fields (e.g. legacy users from
  // before phoneNumber became mandatory).
  profileComplete: boolean;
  refreshAdminClaim: (forceRefresh?: boolean) => Promise<boolean>;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signInAnonymous: () => Promise<void>;
  // Upgrade an anonymous user to a real account, preserving UID + their trial session.
  upgradeAnonToEmail: (email: string, password: string) => Promise<void>;
  upgradeAnonToGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resendVerification: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [tokenIsAdmin, setTokenIsAdmin] = useState<boolean | null>(null);
  const [adminClaimLoading, setAdminClaimLoading] = useState(false);
  // Which uid the delivered profile / resolved claim belong to. Until they
  // match the current user, gates must keep showing the loading state —
  // otherwise the render right after sign-in resolves (before the effects
  // below run) looks "resolved" with no profile and a stale claim, and the
  // admin gate bounces admins to the dashboard.
  const [profileUid, setProfileUid] = useState<string | null>(null);
  const [claimUid, setClaimUid] = useState<string | null>(null);
  const [adminClaimError, setAdminClaimError] = useState<string | null>(null);
  const [claimRefreshAttemptedForUid, setClaimRefreshAttemptedForUid] = useState<string | null>(null);

  const refreshAdminClaim = useCallback(async (forceRefresh = false): Promise<boolean> => {
    const current = auth.currentUser;
    if (!current || current.isAnonymous) {
      setTokenIsAdmin(false);
      setClaimUid(current?.uid ?? null);
      setAdminClaimLoading(false);
      setAdminClaimError(null);
      return false;
    }

    setAdminClaimLoading(true);
    setAdminClaimError(null);
    try {
      const token = await current.getIdTokenResult(forceRefresh);
      const nextIsAdmin = token.claims.admin === true;
      setTokenIsAdmin(nextIsAdmin);
      setClaimUid(current.uid);
      return nextIsAdmin;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not read admin token claim.';
      console.error('[auth] admin token claim error:', err);
      setTokenIsAdmin(false);
      setClaimUid(current.uid);
      setAdminClaimError(message);
      return false;
    } finally {
      setAdminClaimLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) setProfile(null);
    });
    return unsub;
  }, []);

  useEffect(() => {
    setClaimRefreshAttemptedForUid(null);
    setTokenIsAdmin(null);
    if (!user || user.isAnonymous) {
      setTokenIsAdmin(false);
      setAdminClaimLoading(false);
      setAdminClaimError(null);
      return;
    }
    void refreshAdminClaim(false);
  }, [user?.uid, user?.isAnonymous, refreshAdminClaim]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileUid(null);
      return;
    }
    setProfileLoading(true);
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (snap.exists()) {
          setProfile(snap.data() as UserProfile);
        } else {
          // No user doc yet — bootstrap creates it (idempotent).
          setProfile(null);
        }
        setProfileUid(user.uid);
        setProfileLoading(false);
      },
      (err) => {
        console.error('[auth] profile snapshot error:', err);
        setProfileUid(user.uid);
        setProfileLoading(false);
      },
    );
    return unsub;
  }, [user]);

  // Bootstrap the user doc on first non-anonymous sign-in. Idempotent
  // server-side: a no-op if the doc already exists. Anonymous users have no
  // user doc (their trial state lives only on the examSessions doc).
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    callBootstrapUserProfile({}).catch((err) => {
      console.warn('[auth] bootstrapUserProfile failed:', err);
    });
  }, [user]);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    if (profile?.role !== 'admin') return;
    if (tokenIsAdmin !== false) return;
    if (claimRefreshAttemptedForUid === user.uid) return;

    setClaimRefreshAttemptedForUid(user.uid);
    void refreshAdminClaim(true);
  }, [claimRefreshAttemptedForUid, profile?.role, refreshAdminClaim, tokenIsAdmin, user]);

  const registered = user !== null && !user.isAnonymous;
  const profileReady = !registered || profileUid === user.uid;
  const claimReady = !registered || claimUid === user.uid;
  const value: AuthState = {
    user,
    profile: registered && !profileReady ? null : profile,
    loading: authLoading || (registered && (profileLoading || !profileReady)),
    isAdmin: claimReady && tokenIsAdmin === true,
    profileIsAdmin: profile?.role === 'admin',
    tokenIsAdmin: claimReady ? tokenIsAdmin : null,
    adminClaimLoading: adminClaimLoading || (registered && !claimReady),
    adminClaimError,
    isAnonymous: user?.isAnonymous === true,
    // Anonymous users have no email to verify — bypass the gate. Email-based
    // and Google users still need a verified address before app access.
    needsEmailVerification: user !== null && !user.isAnonymous && !user.emailVerified,
    profileComplete:
      (profile?.displayName?.trim().length ?? 0) >= 2 &&
      (profile?.phoneNumber?.replace(/\D/g, '').length ?? 0) === 10 &&
      (profile?.state?.length ?? 0) > 0,
    async signInEmail(email, password) {
      if (auth.currentUser && !auth.currentUser.isAnonymous) return;
      await signInWithEmailAndPassword(auth, email, password);
    },
    async signUpEmail(email, password) {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      try {
        if (USE_BRANDED_VERIFICATION_EMAIL) {
          const { callSendVerificationEmail } = await import('./api');
          await callSendVerificationEmail({});
        } else {
          await sendEmailVerification(cred.user);
        }
      } catch (sendErr) {
        console.warn('[auth] verification email failed (signup):', sendErr);
      }
    },
    async signInGoogle() {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    },
    async signInAnonymous() {
      await signInAnonymously(auth);
    },
    async upgradeAnonToEmail(email, password) {
      if (!auth.currentUser) throw new Error('No current user to upgrade.');
      if (!auth.currentUser.isAnonymous) throw new Error('Current user is not anonymous.');
      const credential = EmailAuthProvider.credential(email, password);
      await linkWithCredential(auth.currentUser, credential);
      try {
        if (USE_BRANDED_VERIFICATION_EMAIL) {
          const { callSendVerificationEmail } = await import('./api');
          await callSendVerificationEmail({});
        } else if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser);
        }
      } catch (sendErr) {
        console.warn('[auth] verification email failed (upgrade):', sendErr);
      }
    },
    async upgradeAnonToGoogle() {
      if (!auth.currentUser) throw new Error('No current user to upgrade.');
      if (!auth.currentUser.isAnonymous) throw new Error('Current user is not anonymous.');
      const provider = new GoogleAuthProvider();
      await linkWithPopup(auth.currentUser, provider);
    },
    async signOut() {
      await fbSignOut(auth);
      // Drop every cached query (mastery, sessions, exam slates, etc.) so the
      // next signed-in user never sees a flash of the previous user's data.
      // Without this, fragments computed from useMemo on stale query data can
      // briefly display before the per-uid queryKey re-fetches.
      queryClient.clear();
    },
    async resendVerification() {
      if (!auth.currentUser || auth.currentUser.emailVerified) return;
      if (USE_BRANDED_VERIFICATION_EMAIL) {
        const { callSendVerificationEmail } = await import('./api');
        await callSendVerificationEmail({});
      } else {
        await sendEmailVerification(auth.currentUser);
      }
    },
    refreshAdminClaim,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
